import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/api/client";
import { fmtCurrency, fmtDate } from "@/lib/constants";
import { Receipt, Plus, X, Trash2, Pencil, Wallet } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

const CATEGORIES = ["travel", "fuel", "food", "spare", "toll", "lodging", "misc"];
const STATUSES = ["pending", "approved", "rejected", "reimbursed"];
const STATUS_STYLES = {
  pending:    "bg-slate-500/10 text-slate-500",
  approved:   "bg-blue-500/10 text-blue-500",
  rejected:   "bg-red-500/10 text-red-500",
  reimbursed: "bg-emerald-500/10 text-emerald-500",
};

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [totals, setTotals] = useState({ total: 0, by_status: {} });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const [r, e, t] = await Promise.all([
        api.get(`/expenses${q}`),
        api.get("/users?role=engineer"),
        api.get("/expenses/summary/totals"),
      ]);
      setItems(r.data); setEngineers(e.data); setTotals(t.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const ids = items.map((i) => i.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const arr = Array.from(selected);
    if (!window.confirm(`Delete ${arr.length} expense(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(arr, (id) => api.delete(`/expenses/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < arr.length) setErr(`${arr.length - ok} deletion(s) failed.`);
  };

  const tiles = useMemo(() => ([
    { key: "total",      label: "Total", value: totals.total },
    { key: "pending",    label: "Pending",    value: totals.by_status?.pending || 0 },
    { key: "approved",   label: "Approved",   value: totals.by_status?.approved || 0 },
    { key: "reimbursed", label: "Reimbursed", value: totals.by_status?.reimbursed || 0 },
  ]), [totals]);

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Finance</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Expenses</h1>
          <p className="mt-1 text-sm text-muted-foreground">Track engineer travel, fuel, spares and other reimbursements.</p>
        </div>
        <button data-testid="expense-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
          <Plus className="h-3.5 w-3.5" /> New expense
        </button>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.key} data-testid={`expense-tile-${t.key}`} className="sharp-card p-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary"><Wallet className="h-5 w-5" /></div>
            <p className="label-eyebrow mt-3">{t.label}</p>
            <p className="mt-1 font-heading text-2xl font-bold">{fmtCurrency(t.value)}</p>
          </div>
        ))}
      </section>

      <div className="mb-4 flex flex-wrap gap-2">
        <button data-testid="expense-filter-all" onClick={() => setStatusFilter("")}
          className={`rounded-md border px-3 py-1.5 text-xs font-medium ${!statusFilter ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>All</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`expense-filter-${s}`} onClick={() => setStatusFilter(s)}
            className={`rounded-md border px-3 py-1.5 text-xs font-medium capitalize ${statusFilter === s ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>{s}</button>
        ))}
      </div>

      <div className="sharp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-secondary/30 text-left">
            <tr>
              <th className="w-10 px-4 py-2">
                <RowCheckbox testid="expense-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
              </th>
              <th className="px-4 py-2 label-eyebrow">Date</th>
              <th className="px-4 py-2 label-eyebrow">Category</th>
              <th className="px-4 py-2 label-eyebrow">Engineer</th>
              <th className="px-4 py-2 label-eyebrow">Description</th>
              <th className="px-4 py-2 label-eyebrow text-right">Amount</th>
              <th className="px-4 py-2 label-eyebrow">Status</th>
              <th className="px-4 py-2 label-eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                <td className="px-4 py-2">
                  <RowCheckbox testid={`expense-select-${e.id}`} checked={selected.has(e.id)} onChange={() => toggleOne(e.id)} />
                </td>
                <td className="px-4 py-2 text-muted-foreground">{fmtDate(e.date)}</td>
                <td className="px-4 py-2 capitalize">
                  <span className="inline-flex items-center gap-1"><Receipt className="h-3.5 w-3.5 text-muted-foreground" />{e.category}</span>
                </td>
                <td className="px-4 py-2">{e.engineer_name || "—"}</td>
                <td className="px-4 py-2 max-w-xs truncate text-muted-foreground">{e.description || "—"}</td>
                <td className="px-4 py-2 text-right font-semibold">{fmtCurrency(e.amount)}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[e.status] || STATUS_STYLES.pending}`}>{e.status}</span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex gap-1">
                    <button data-testid={`expense-edit-${e.id}`} onClick={() => setEditing(e)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`expense-delete-${e.id}`}
                      onClick={async () => { if (!window.confirm("Delete this expense?")) return; try { await api.delete(`/expenses/${e.id}`); await load(); } catch (er) { setErr(formatApiError(er)); } }}
                      className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-muted-foreground">No expenses logged yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <BulkBar count={selected.size} entityLabel="expenses" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <ExpenseModal engineers={engineers} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <ExpenseModal existing={editing} engineers={engineers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ExpenseModal({ engineers, onClose, onSaved, existing }) {
  const [form, setForm] = useState({
    date: existing?.date || new Date().toISOString().slice(0, 10),
    category: existing?.category || "travel",
    amount: existing?.amount ?? 0,
    description: existing?.description || "",
    engineer_id: existing?.engineer_id || "",
    status: existing?.status || "pending",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (!payload.engineer_id) delete payload.engineer_id;
      if (existing) await api.patch(`/expenses/${existing.id}`, payload);
      else await api.post("/expenses", payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg sharp-card" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">{existing ? "Edit expense" : "New expense"}</h3>
          <button data-testid="expense-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <div>
            <label className="label-eyebrow mb-1.5 block">Date *</label>
            <input data-testid="expense-form-date" required type="date" value={form.date} onChange={set("date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Category *</label>
            <select data-testid="expense-form-category" required value={form.category} onChange={set("category")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm capitalize">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Amount (INR) *</label>
            <input data-testid="expense-form-amount" required type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Engineer</label>
            <select data-testid="expense-form-engineer" value={form.engineer_id} onChange={set("engineer_id")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">— Me —</option>
              {engineers.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Description</label>
            <textarea data-testid="expense-form-description" value={form.description} onChange={set("description")} rows={2} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Status</label>
            <select data-testid="expense-form-status" value={form.status} onChange={set("status")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm capitalize">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="expense-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create expense")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
